import React from 'react';
import { Container, Row, Col, Button, Card } from 'react-bootstrap';
import { FiRefreshCw, FiDollarSign, FiSmartphone, FiTag, FiShoppingCart, FiBarChart2 } from 'react-icons/fi';
import { BsCloudUpload, BsBatteryFull, BsQrCodeScan } from 'react-icons/bs';

const HanshowESL = () => {
  // Sample product data - replace with actual products
  const products = [
    { id: 1, name: 'Stellar Series', desc: 'High-resolution e-ink for premium retail', image: '/placeholder-stellar.jpg' },
    { id: 2, name: 'Nebular Series', desc: 'Color e-ink for promotions', image: '/placeholder-nebular.jpg' },
    { id: 3, name: 'Galaxy Series', desc: 'Basic ESL for supermarkets', image: '/placeholder-galaxy.jpg' },
    { id: 4, name: 'Pulsar NFC', desc: 'NFC-enabled interactive labels', image: '/placeholder-pulsar.jpg' },
  ];

  return (
    <div className="hanshow-esl-page">
      {/* Hero Section */}
      <section className="hero-section text-white py-5" style={{ background: 'linear-gradient(135deg, #1e3a8a 0%, #2563eb 100%)' }}>
        <Container>
          <Row className="align-items-center">
            <Col lg={6} className="mb-4 mb-lg-0">
              <h1 className="display-4 fw-bold mb-3">Revolutionize Your Retail with Hanshow Electronic Shelf Labels</h1>
              <p className="lead mb-4">Dynamic pricing, real-time updates, and operational efficiency — all in one smart label.</p>
              <div className="d-flex gap-3">
                <Button variant="light" size="lg" className="fw-bold">Request a Demo</Button>
                <Button variant="outline-light" size="lg">Get a Quote</Button>
              </div>
            </Col>
            <Col lg={6}>
              <div className="hero-image rounded overflow-hidden shadow-lg">
                <img 
                  src="/hanshow-esl-hero.jpg" 
                  alt="Hanshow ESL in retail environment" 
                  className="img-fluid" 
                  style={{ objectFit: 'cover', width: '100%', height: '100%' }}
                />
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* What is Hanshow ESL */}
      <section className="py-5 bg-light">
        <Container>
          <Row className="mb-5">
            <Col className="text-center">
              <h2 className="fw-bold">What is Hanshow ESL?</h2>
              <p className="lead text-muted">Digital price tags transforming retail operations</p>
            </Col>
          </Row>
          <Row className="g-4">
            <Col md={4}>
              <div className="text-center p-4">
                <div className="icon-box bg-primary bg-opacity-10 text-primary rounded-circle mx-auto mb-4" style={{ width: '80px', height: '80px', lineHeight: '80px' }}>
                  <FiRefreshCw size={32} />
                </div>
                <h4>Real-time Updates</h4>
                <p className="text-muted">Change prices instantly across all stores from a central system</p>
              </div>
            </Col>
            <Col md={4}>
              <div className="text-center p-4">
                <div className="icon-box bg-success bg-opacity-10 text-success rounded-circle mx-auto mb-4" style={{ width: '80px', height: '80px', lineHeight: '80px' }}>
                  <FiDollarSign size={32} />
                </div>
                <h4>Energy Efficient</h4>
                <p className="text-muted">E-ink technology uses power only when changing display</p>
              </div>
            </Col>
            <Col md={4}>
              <div className="text-center p-4">
                <div className="icon-box bg-info bg-opacity-10 text-info rounded-circle mx-auto mb-4" style={{ width: '80px', height: '80px', lineHeight: '80px' }}>
                  <FiSmartphone size={32} />
                </div>
                <h4>Cloud Management</h4>
                <p className="text-muted">Control your entire ESL network from anywhere</p>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Key Features */}
      <section className="py-5">
        <Container>
          <Row className="mb-5">
            <Col className="text-center">
              <h2 className="fw-bold">Key Features</h2>
              <p className="lead text-muted">Everything you need for modern retail pricing</p>
            </Col>
          </Row>
          <Row className="g-4">
            <Col md={6} lg={4}>
              <div className="feature-card p-4 rounded-3 shadow-sm h-100">
                <div className="d-flex align-items-center mb-3">
                  <FiTag className="text-primary me-3" size={24} />
                  <h5 className="mb-0">Responsive Pricing</h5>
                </div>
                <p className="text-muted">React instantly to market changes, promotions, or demand fluctuations with price updates in seconds.</p>
              </div>
            </Col>
            <Col md={6} lg={4}>
              <div className="feature-card p-4 rounded-3 shadow-sm h-100">
                <div className="d-flex align-items-center mb-3">
                  <BsCloudUpload className="text-primary me-3" size={24} />
                  <h5 className="mb-0">POS/ERP Integration</h5>
                </div>
                <p className="text-muted">Seamless connection with your existing retail systems for automatic price synchronization.</p>
              </div>
            </Col>
            <Col md={6} lg={4}>
              <div className="feature-card p-4 rounded-3 shadow-sm h-100">
                <div className="d-flex align-items-center mb-3">
                  <FiShoppingCart className="text-primary me-3" size={24} />
                  <h5 className="mb-0">Multiple Sizes</h5>
                </div>
                <p className="text-muted">Variety of display sizes to fit any product, from small cosmetics to large shelf-edge labels.</p>
              </div>
            </Col>
            <Col md={6} lg={4}>
              <div className="feature-card p-4 rounded-3 shadow-sm h-100">
                <div className="d-flex align-items-center mb-3">
                  <BsQrCodeScan className="text-primary me-3" size={24} />
                  <h5 className="mb-0">NFC/QR Enabled</h5>
                </div>
                <p className="text-muted">Interactive labels that connect with customer smartphones for enhanced shopping experiences.</p>
              </div>
            </Col>
            <Col md={6} lg={4}>
              <div className="feature-card p-4 rounded-3 shadow-sm h-100">
                <div className="d-flex align-items-center mb-3">
                  <BsBatteryFull className="text-primary me-3" size={24} />
                  <h5 className="mb-0">5+ Year Battery</h5>
                </div>
                <p className="text-muted">Ultra-low power consumption means years of maintenance-free operation.</p>
              </div>
            </Col>
            <Col md={6} lg={4}>
              <div className="feature-card p-4 rounded-3 shadow-sm h-100">
                <div className="d-flex align-items-center mb-3">
                  <FiBarChart2 className="text-primary me-3" size={24} />
                  <h5 className="mb-0">Cloud Management</h5>
                </div>
                <p className="text-muted">Centralized dashboard to control thousands of labels across multiple locations.</p>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Benefits Section */}
      <section className="py-5 bg-light">
        <Container>
          <Row className="mb-5">
            <Col className="text-center">
              <h2 className="fw-bold">Benefits for Retailers</h2>
              <p className="lead text-muted">Transform your retail operations with digital shelf labels</p>
            </Col>
          </Row>
          <Row className="g-4">
            <Col md={6} lg={3}>
              <Card className="h-100 border-0 shadow-sm">
                <Card.Body className="text-center p-4">
                  <div className="bg-primary bg-opacity-10 text-primary rounded-circle mx-auto mb-4" style={{ width: '60px', height: '60px', lineHeight: '60px' }}>
                    <FiDollarSign size={24} />
                  </div>
                  <h5>Reduce Labor Costs</h5>
                  <p className="text-muted">Eliminate manual price tag changes saving hundreds of labor hours annually.</p>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} lg={3}>
              <Card className="h-100 border-0 shadow-sm">
                <Card.Body className="text-center p-4">
                  <div className="bg-success bg-opacity-10 text-success rounded-circle mx-auto mb-4" style={{ width: '60px', height: '60px', lineHeight: '60px' }}>
                    <FiTag size={24} />
                  </div>
                  <h5>Eliminate Pricing Errors</h5>
                  <p className="text-muted">Ensure 100% accuracy between shelf prices and POS system.</p>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} lg={3}>
              <Card className="h-100 border-0 shadow-sm">
                <Card.Body className="text-center p-4">
                  <div className="bg-info bg-opacity-10 text-info rounded-circle mx-auto mb-4" style={{ width: '60px', height: '60px', lineHeight: '60px' }}>
                    <FiShoppingCart size={24} />
                  </div>
                  <h5>Improve Customer Experience</h5>
                  <p className="text-muted">Dynamic displays with promotions and product info enhance shopping.</p>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} lg={3}>
              <Card className="h-100 border-0 shadow-sm">
                <Card.Body className="text-center p-4">
                  <div className="bg-warning bg-opacity-10 text-warning rounded-circle mx-auto mb-4" style={{ width: '60px', height: '60px', lineHeight: '60px' }}>
                    <FiRefreshCw size={24} />
                  </div>
                  <h5>Dynamic Promotions</h5>
                  <p className="text-muted">Run flash sales and time-sensitive promotions with instant shelf updates.</p>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Product Range */}
      <section className="py-5">
        <Container>
          <Row className="mb-5">
            <Col className="text-center">
              <h2 className="fw-bold">Hanshow Product Range</h2>
              <p className="lead text-muted">Find the perfect ESL solution for your retail environment</p>
            </Col>
          </Row>
          <Row className="g-4">
            {products.map(product => (
              <Col key={product.id} md={6} lg={3}>
                <Card className="h-100 border-0 shadow-sm overflow-hidden">
                  <div className="product-image-container" style={{ height: '200px', overflow: 'hidden' }}>
                    <Card.Img 
                      variant="top" 
                      src={product.image} 
                      alt={product.name}
                      style={{ objectFit: 'cover', height: '100%', width: '100%' }}
                    />
                  </div>
                  <Card.Body>
                    <h5>{product.name}</h5>
                    <p className="text-muted">{product.desc}</p>
                  </Card.Body>
                  <Card.Footer className="bg-white border-0">
                    <Button variant="outline-primary" className="w-100">View Details</Button>
                  </Card.Footer>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* How It Works */}
      <section className="py-5 bg-light">
        <Container>
          <Row className="mb-5">
            <Col className="text-center">
              <h2 className="fw-bold">How It Works</h2>
              <p className="lead text-muted">Simple integration, powerful results</p>
            </Col>
          </Row>
          <Row className="g-4 text-center">
            <Col md={4}>
              <div className="step-number mb-3 mx-auto" style={{ width: '60px', height: '60px', lineHeight: '60px', background: '#2563eb', color: 'white', borderRadius: '50%', fontSize: '24px', fontWeight: 'bold' }}>1</div>
              <h4>Integrate with POS</h4>
              <p className="text-muted">Connect Hanshow system with your existing point-of-sale or ERP</p>
            </Col>
            <Col md={4}>
              <div className="step-number mb-3 mx-auto" style={{ width: '60px', height: '60px', lineHeight: '60px', background: '#2563eb', color: 'white', borderRadius: '50%', fontSize: '24px', fontWeight: 'bold' }}>2</div>
              <h4>Update Prices</h4>
              <p className="text-muted">Make changes through the cloud-based management dashboard</p>
            </Col>
            <Col md={4}>
              <div className="step-number mb-3 mx-auto" style={{ width: '60px', height: '60px', lineHeight: '60px', background: '#2563eb', color: 'white', borderRadius: '50%', fontSize: '24px', fontWeight: 'bold' }}>3</div>
              <h4>Instant Display</h4>
              <p className="text-muted">Labels update automatically within seconds across all locations</p>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Why Choose Us */}
      <section className="py-5">
        <Container>
          <Row className="mb-5">
            <Col className="text-center">
              <h2 className="fw-bold">Why Choose Zartec Solutions</h2>
              <p className="lead text-muted">Your trusted Hanshow ESL partner in the region</p>
            </Col>
          </Row>
          <Row className="g-4">
            <Col md={4}>
              <div className="p-4 rounded-3 bg-light h-100">
                <h4 className="text-primary">Expert Implementation</h4>
                <p>Our certified technicians ensure seamless integration with your existing retail systems.</p>
              </div>
            </Col>
            <Col md={4}>
              <div className="p-4 rounded-3 bg-light h-100">
                <h4 className="text-primary">Local Support</h4>
                <p>Dedicated regional support team for installation, training, and maintenance.</p>
              </div>
            </Col>
            <Col md={4}>
              <div className="p-4 rounded-3 bg-light h-100">
                <h4 className="text-primary">Custom Solutions</h4>
                <p>Tailored ESL configurations to match your specific retail environment and needs.</p>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* CTA Section */}
      <section className="py-5 bg-dark text-white">
        <Container>
          <Row className="align-items-center">
            <Col lg={8} className="mb-4 mb-lg-0">
              <h3 className="fw-bold mb-3">Ready to transform your retail pricing?</h3>
              <p className="mb-0">Contact our ESL specialists today to discuss your requirements</p>
            </Col>
            <Col lg={4} className="text-lg-end">
              <Button variant="light" size="lg" className="me-2">Get a Quote</Button>
              <Button variant="outline-light" size="lg">Contact Sales</Button>
            </Col>
          </Row>
        </Container>
      </section>
    </div>
  );
};

export default HanshowESL;